import React, { useState, useRef, useCallback } from "react";
import axios from "axios";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import useFetch from "../../hooks/useFetch";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Alert, AlertTitle, DialogContent, DialogContentText } from "@mui/material";
import { cancelUploadAPI } from "./MuxCancelUploadAction";
import { toast } from 'react-toastify';

const MuxUploadAction = ({ item, onSuccess, onUploadStatusChange }: { item: any; onSuccess?: (data: any) => void; onUploadStatusChange?: (isUploading: boolean) => void; }) => {
  const [progress, setProgress] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { fetchData: fetchCreateUpload } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/performances/mux/create-upload`,
    method: "POST",
  });

  const handleUploadClick = () => fileInputRef.current?.click();

  const isIOS = (): boolean => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  };

  const isVideoRecording = (file: File): boolean => {
    const fileName = file.name.toLowerCase();
    const isRecentFile = Date.now() - file.lastModified < 10000;
    
    const recordingPatterns = [
      'rpreplay',
      'trim.',
      'video_',
      'mov_',
      'img_'
    ];
    
    const hasRecordingPattern = recordingPatterns.some(pattern => 
      fileName.includes(pattern)
    );
    
    return hasRecordingPattern || (isRecentFile && fileName.endsWith('.mov'));
  };

  const downloadVideoToDevice = (file: File) => {
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const getActiveVideos = (): Record<string, any> => JSON.parse(localStorage.getItem("active_videos") || "{}");

  const getUploadHistory = (): Record<string, any> => {
    const history = JSON.parse(localStorage.getItem("upload_history") || "{}");
    const now = Date.now();
    const cleanedHistory = { ...history };
    let updated = false;
    
    Object.entries(history).forEach(([id, data]: [string, any]) => {
      if (data.removedAt && data.removedAt < now) {
        delete cleanedHistory[id];
        updated = true;
      }
    });
    
    if (updated) {
      localStorage.setItem("upload_history", JSON.stringify(cleanedHistory));
    }
    
    return cleanedHistory;
  };

  const setUploadHistory = (performanceId: number, status: string, keepForMs: number) => {
    const history = getUploadHistory();
    history[performanceId] = {
      status: status,
      userId: JSON.parse(localStorage.getItem('user') || '{}').id,
      entryNumber: item.entry_number,
      removedAt: Date.now() + keepForMs
    };
    
    localStorage.setItem("upload_history", JSON.stringify(history));
  };

  const addToActiveVideos = (performanceId: number, entryNumber: number) => {
    const activeVideos = getActiveVideos();

    activeVideos[performanceId] = {
      status: "uploading",
      entry_number: entryNumber,
    };
    localStorage.setItem("active_videos", JSON.stringify(activeVideos));
    setUploadHistory(performanceId, "uploading", 0);
  };

  const removeFromActiveVideos = (performanceId: number) => {
    const activeVideos = getActiveVideos();
    delete activeVideos[performanceId];
    localStorage.setItem("active_videos", JSON.stringify(activeVideos));
  };

  const uploadFile = async (file: File) => {
    try {
    
    addToActiveVideos(item.performance_id, item.entry_number);

      const result = await fetchCreateUpload({ 
        performance_id: item.performance_id,
        entry_number: item.entry_number,
        talktape_video_title: `${item.talktape_video_title}_${file.name}`
      });
      
      const uploadUrl = result?.data?.directuploadurl;
        if (!result?.data?.directuploadurl && result?.success === false) {
        removeFromActiveVideos(item.performance_id);
        setUploadHistory(item.performance_id, "failed", 30 * 60 * 1000);
        throw new Error(result?.message || 'No upload URL returned from server');
      }

      await axios.put(uploadUrl, file, {
        headers: { "Content-Type": file.type },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && progressEvent.lengthComputable) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setProgress(percentCompleted);

          }
        },
      });

      setProgress(100);
      removeFromActiveVideos(item.performance_id);
      setUploadHistory(item.performance_id, "completed", 10000);
      setTimeout(() => {
        if (onSuccess) {
          onSuccess(result.data.performance_talktape);
        }
        setProgress(0);
      }, 500);
    } catch (err: unknown) {
      removeFromActiveVideos(item.performance_id);
      const error = err instanceof Error ? err.message : "Upload failed";
      console.log(error);
      if(error.includes("A talktape video upload is already in progress for this performance")){
        const history = JSON.parse(localStorage.getItem("upload_history") || "{}");
        delete history[item.performance_id];
        localStorage.setItem("upload_history", JSON.stringify(history));
        alert(error);
      }
      else if (error.includes("Request failed with status code 400")){
        toast.warn("Free plan is limited to 10 assets, you cannot create direct uploads while exceeding this limit");
        console.error("Free plan is limited to 10 assets, you cannot create direct uploads while exceeding this limit");
        cancelUploadAPI(item.performance_id, item);
      }
      else{
        toast.warn(error);
        console.error(error);
        cancelUploadAPI(item.performance_id, item);
      }
      setProgress(0);
    } finally {
      if (onUploadStatusChange) onUploadStatusChange(false);
    }
  }

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (isIOS() && isVideoRecording(file)) {
      downloadVideoToDevice(file);
      
      setPendingFile(file);
      setShowConfirmDialog(true);
    } else {
      await uploadFile(file);
    }

    e.target.value = '';
  }, [fetchCreateUpload, item.performance_id, item.talktape_video_title, onSuccess, uploadFile]);

  const handleConfirmUpload = async () => {
    setShowConfirmDialog(false);
    if (pendingFile) {
      await uploadFile(pendingFile);
      setPendingFile(null);
    }
    if (onUploadStatusChange) onUploadStatusChange(false);
  };

  return (
    <div className="upload-wrapper">
      <input
        type="file"
        accept="video/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      <Tooltip title="Upload Talktape" enterDelay={0} leaveDelay={0}>
        <span>
          <FileUploadOutlinedIcon
            className="music-icon upload"
            onClick={handleUploadClick}
            style={{ opacity: progress > 0 ? 0.5 : 1 }}
          />
        </span>
      </Tooltip>
      {progress > 0 && <span className="upload-progress">{progress}%</span>}

      
      <Dialog
        open={showConfirmDialog}
        onClose={handleConfirmUpload}
        aria-labelledby="video-upload-dialog"
      >
        <DialogTitle id="video-upload-dialog">Video Recorded!</DialogTitle>

        <DialogContent>
          <Alert
            severity="warning"
            icon={<WarningAmberIcon fontSize="inherit" />}
            sx={{ mb: 2 }}
          >
            <AlertTitle>Video Recorded</AlertTitle>
            Video recorded. Please save it using <strong>"Save In..."</strong>.  
            If you don't save it to Files/Downloads, it cannot be retrieved later and
            you won't be able to reupload it.
          </Alert>

          <DialogContentText>
            Please press **OK** to continue.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleConfirmUpload}
            color="primary"
            variant="contained"
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MuxUploadAction;